import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography
} from "@material-ui/core";
import { withAlert } from "react-alert";
import { DebounceInput } from "react-debounce-input";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import Button from "../../../waybee-ui/Button";
import AclManagementService from "../../../services/AclManagementService";
import { getCounterpartCode } from "../AccessControl/buildPermissionTree";

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(300px, 1.2fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  min-height: 360px;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const PanelTitle = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 16px;
    color: #333;
  }
`;

const PanelHint = styled(Typography)`
  && {
    color: #777;
    font-size: 13px;
    margin-top: 4px;
  }
`;

const PanelBody = styled.div`
  padding: 12px 16px 16px;
  flex: 1;
  overflow: auto;
`;

const ClassItem = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.color.primary.main : "#eee")};
  background: ${({ $active, theme }) =>
    $active ? theme.color.primary.light : "#fff"};
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 8px;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.color.primary.main};
  }
`;

const ClassName = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 14px;
    color: #2f2f2f;
  }
`;

const ClassMeta = styled(Typography)`
  && {
    color: #777;
    font-size: 12px;
    margin-top: 4px;
  }
`;

const RoleRow = styled.div`
  border: 1px solid
    ${({ $checked, theme }) => ($checked ? theme.color.primary.main : "#eee")};
  background: ${({ $checked, theme }) =>
    $checked ? theme.color.primary.light : "#fafafa"};
  border-radius: 8px;
  padding: 4px 10px;
  margin-bottom: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  border: 1px dashed #d8d8d8;
  border-radius: 8px;
  background: #fafafa;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  margin-top: 12px;
`;

class ClassAccess extends Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: [],
      roles: [],
      selectedClassId: null,
      selectedRoleIds: new Set(),
      initialRoleIds: [],
      searchText: "",
      loadingClasses: false,
      loadingRoles: false,
      loadingLinks: false,
      saving: false
    };
  }

  componentDidMount() {
    this.loadInitialData();
  }

  loadInitialData = async () => {
    const { alert, t } = this.props;
    this.setState({ loadingClasses: true, loadingRoles: true });
    try {
      const [classes, roles] = await Promise.all([
        AclManagementService.getClasses(),
        AclManagementService.getRoles(true)
      ]);
      this.setState({
        classes: Array.isArray(classes) ? classes : [],
        roles: Array.isArray(roles) ? roles : [],
        loadingClasses: false,
        loadingRoles: false
      });
    } catch (e) {
      console.error(e);
      alert.show(t("settings.classAccess.loadError"), {
        title: t("common.actions.error")
      });
      this.setState({
        classes: [],
        roles: [],
        loadingClasses: false,
        loadingRoles: false
      });
    }
  };

  selectClass = async classItem => {
    const { alert, t } = this.props;
    this.setState({
      selectedClassId: classItem.id,
      loadingLinks: true,
      selectedRoleIds: new Set(),
      initialRoleIds: []
    });
    try {
      const linkedRoles = await AclManagementService.getClassRoles(
        classItem.id
      );
      const initialRoleIds = (linkedRoles || []).map(role => Number(role.id));
      this.setState({
        selectedRoleIds: new Set(initialRoleIds),
        initialRoleIds,
        loadingLinks: false
      });
    } catch (e) {
      console.error(e);
      alert.show(t("settings.classAccess.loadClassRolesError"), {
        title: t("common.actions.error")
      });
      this.setState({ loadingLinks: false });
    }
  };

  toggleRole = roleId => {
    const id = Number(roleId);
    this.setState(prev => {
      const selectedRoleIds = new Set(prev.selectedRoleIds);
      if (selectedRoleIds.has(id)) {
        selectedRoleIds.delete(id);
      } else {
        selectedRoleIds.add(id);
      }
      return { selectedRoleIds };
    });
  };

  hasChanges = () => {
    const { selectedRoleIds, initialRoleIds } = this.state;
    if (selectedRoleIds.size !== initialRoleIds.length) return true;
    return initialRoleIds.some(id => !selectedRoleIds.has(id));
  };

  handleSave = async () => {
    const { selectedClassId, selectedRoleIds } = this.state;
    const { alert, t } = this.props;
    if (!selectedClassId) return;

    this.setState({ saving: true });
    try {
      await AclManagementService.saveClassRoles({
        classId: selectedClassId,
        roleIds: Array.from(selectedRoleIds)
      });
      const initialRoleIds = Array.from(selectedRoleIds);
      this.setState({
        initialRoleIds,
        selectedRoleIds: new Set(initialRoleIds),
        saving: false
      });
      alert.show(t("settings.classAccess.saved"), {
        title: t("common.actions.success")
      });
    } catch (e) {
      console.error(e);
      const message =
        (e.response &&
          e.response.data &&
          e.response.data.error &&
          e.response.data.error.message) ||
        t("settings.classAccess.saveError");
      alert.show(message, { title: t("common.actions.error") });
      this.setState({ saving: false });
    }
  };

  getFilteredClasses = () => {
    const { classes, searchText } = this.state;
    const query = searchText.trim().toLowerCase();
    if (!query) return classes;
    return classes.filter(item => {
      const name = (item.name || "").toLowerCase();
      const id = String(item.id || "").toLowerCase();
      const period = (item.period || "").toLowerCase();
      return (
        name.includes(query) || id.includes(query) || period.includes(query)
      );
    });
  };

  renderClassesBody = (filtered, loadingClasses, selectedClassId) => {
    const { t } = this.props;

    if (loadingClasses) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (filtered.length === 0) {
      return (
        <EmptyState>
          <Typography style={{ fontWeight: 600, marginBottom: 6 }}>
            {t("settings.classAccess.noClasses")}
          </Typography>
          <Typography color="textSecondary">
            {t("settings.classAccess.noClassesHint")}
          </Typography>
        </EmptyState>
      );
    }

    return filtered.map(classItem => (
      <ClassItem
        key={classItem.key || classItem.id}
        type="button"
        $active={selectedClassId === classItem.id}
        onClick={() => this.selectClass(classItem)}
      >
        <ClassName>{classItem.name || classItem.id}</ClassName>
        <ClassMeta>
          {t("settings.classAccess.codeLabel")} 
          {' '}
          {classItem.id}
          {classItem.period ? ` · ${classItem.period}` : ""}
        </ClassMeta>
      </ClassItem>
    ));
  };

  renderClassesPanel = () => {
    const { loadingClasses, selectedClassId, searchText } = this.state;
    const { t } = this.props;
    const filtered = this.getFilteredClasses();

    return (
      <Panel>
        <PanelHeader>
          <PanelTitle>{t("settings.classAccess.classes")}</PanelTitle>
          <PanelHint>{t("settings.classAccess.classesHint")}</PanelHint>
          <Box mt={1.5}>
            <DebounceInput
              element={TextField}
              debounceTimeout={300}
              label={t("settings.classAccess.search")}
              variant="outlined"
              size="small"
              fullWidth
              value={searchText}
              onChange={e => this.setState({ searchText: e.target.value })}
            />
          </Box>
        </PanelHeader>
        <PanelBody>
          {this.renderClassesBody(filtered, loadingClasses, selectedClassId)}
        </PanelBody>
      </Panel>
    );
  };

  renderRolesBody = () => {
    const {
      roles,
      selectedRoleIds,
      loadingRoles,
      loadingLinks,
      saving
    } = this.state;
    const { t } = this.props;

    if (loadingRoles || loadingLinks) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (roles.length === 0) {
      return (
        <EmptyState>
          <Typography color="textSecondary">
            {t("settings.classAccess.noProfiles")}
          </Typography>
        </EmptyState>
      );
    }

    return (
      <>
        {roles.map(role => {
          const checked = selectedRoleIds.has(Number(role.id));
          const counterpart = getCounterpartCode(role);
          return (
            <RoleRow key={role.id} $checked={checked}>
              <FormControlLabel
                style={{ margin: 0, width: "100%", alignItems: "flex-start" }}
                control={(
                  <Checkbox
                    color="primary"
                    checked={checked}
                    onChange={() => this.toggleRole(role.id)}
                  />
                )}
                label={(
                  <Box pt={0.5}>
                    <Typography style={{ fontWeight: 600, fontSize: 14 }}>
                      {role.name}
                      {role.isSystem
                        ? t("settings.classAccess.systemSuffix")
                        : ""}
                    </Typography>
                    <Typography color="textSecondary" style={{ fontSize: 12 }}>
                      {role.description || t("settings.profiles.noDescription")}
                      {counterpart
                        ? ` · ${t("settings.profiles.internalCode", {
                            code: counterpart
                          })}`
                        : ""}
                    </Typography>
                  </Box>
                )}
              />
            </RoleRow>
          );
        })}
        <FooterActions>
          <Button
            color="primary"
            variant="contained"
            onClick={this.handleSave}
            disabled={saving || !this.hasChanges()}
          >
            {saving
              ? t("common.actions.saving")
              : t("settings.classAccess.saveLinks")}
          </Button>
        </FooterActions>
      </>
    );
  };

  renderRolesPanel = () => {
    const { selectedClassId } = this.state;
    const { t } = this.props;

    if (!selectedClassId) {
      return (
        <Panel>
          <PanelHeader>
            <PanelTitle>{t("settings.classAccess.profiles")}</PanelTitle>
            <PanelHint>{t("settings.classAccess.sideHint")}</PanelHint>
          </PanelHeader>
          <PanelBody>
            <EmptyState>
              <Typography color="textSecondary">
                {t("settings.classAccess.noneSelected")}
              </Typography>
            </EmptyState>
          </PanelBody>
        </Panel>
      );
    }

    return (
      <Panel>
        <PanelHeader>
          <PanelTitle>{t("settings.classAccess.linked")}</PanelTitle>
          <PanelHint>{t("settings.classAccess.linkedHint")}</PanelHint>
        </PanelHeader>
        <PanelBody>{this.renderRolesBody()}</PanelBody>
      </Panel>
    );
  };

  render() {
    return (
      <Layout>
        {this.renderClassesPanel()}
        {this.renderRolesPanel()}
      </Layout>
    );
  }
}

ClassAccess.propTypes = {
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withTranslation()(ClassAccess));
